import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useEffect, useState } from "react";
import {
  type ExchangeRateRecord,
  listExchangeRates,
  recordExchangeRate,
  resolveExchangeRate,
} from "@/lib/api";

function toErrorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function formatRate(partsPerMillion: number) {
  const sign = partsPerMillion < 0 ? "-" : "";
  const absolute = Math.abs(partsPerMillion);
  const whole = Math.trunc(absolute / 1_000_000);
  const fraction = String(absolute % 1_000_000)
    .padStart(6, "0")
    .replace(/0+$/, "");
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
}

export function CurrencyRatesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [rates, setRates] = useState<ExchangeRateRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [base, setBase] = useState("EUR");
  const [target, setTarget] = useState("USD");
  const [rate, setRate] = useState("");
  const [asOf, setAsOf] = useState(todayIsoDate);
  const [lookupBase, setLookupBase] = useState("EUR");
  const [lookupTarget, setLookupTarget] = useState("USD");
  const [lookupDate, setLookupDate] = useState(todayIsoDate);
  const [lookup, setLookup] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(null);
    listExchangeRates()
      .then((page) => setRates(page.rates))
      .catch((cause: unknown) => setLoadError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!base.trim() || !target.trim() || !rate.trim() || !asOf) {
      setError("Base, target, rate, and date are required.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await recordExchangeRate({
        base_currency: base.trim().toUpperCase(),
        target_currency: target.trim().toUpperCase(),
        rate: rate.trim(),
        as_of: asOf,
      });
      setNotice(
        `Saved ${saved.base_currency} to ${saved.target_currency} at ${formatRate(saved.rate_parts_per_million)} on ${saved.as_of}.`,
      );
      setRate("");
      load();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (event: FormEvent) => {
    event.preventDefault();
    if (!lookupBase.trim() || !lookupTarget.trim() || !lookupDate) {
      setError("Lookup needs both currencies and a date.");
      return;
    }
    setError(null);
    setLookup(null);
    try {
      const result = await resolveExchangeRate({
        base_currency: lookupBase.trim().toUpperCase(),
        target_currency: lookupTarget.trim().toUpperCase(),
        as_of: lookupDate,
      });
      setLookup(
        result.resolution
          ? `${formatRate(result.resolution.rate_parts_per_million)} (${result.resolution.origin}, as of ${result.resolution.as_of})`
          : "No stored rate covers that pair on that date.",
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  return (
    <div className="grid gap-6">
      {embedded ? null : (
        <PageHeader
          title="Currencies & rates"
          description="Enter, review, and look up the exchange rates used to translate the books."
        />
      )}
      <section className="grid gap-4">
        <p className="text-sm text-[var(--muted-foreground)]">
          Enter the rate yourself. Accounting does not download daily rates. One
          unit of the base currency converts to the rate in the target currency.
        </p>
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="text-sm text-[var(--muted-foreground)]">{notice}</p>
        ) : null}
        <form onSubmit={(event) => void save(event)} className="grid gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-xs font-medium" htmlFor="fx-base">
              Base currency
              <Input
                id="fx-base"
                value={base}
                maxLength={3}
                onChange={(event) => setBase(event.target.value)}
              />
            </label>
            <label
              className="grid gap-1 text-xs font-medium"
              htmlFor="fx-target"
            >
              Target currency
              <Input
                id="fx-target"
                value={target}
                maxLength={3}
                onChange={(event) => setTarget(event.target.value)}
              />
            </label>
            <label className="grid gap-1 text-xs font-medium" htmlFor="fx-rate">
              Rate
              <Input
                id="fx-rate"
                inputMode="decimal"
                value={rate}
                onChange={(event) => setRate(event.target.value)}
              />
            </label>
            <label className="grid gap-1 text-xs font-medium" htmlFor="fx-date">
              As of
              <Input
                id="fx-date"
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
              />
            </label>
            <Button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save rate"}
            </Button>
          </div>
        </form>
        {loadError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            <p>Couldn&apos;t load exchange rates: {loadError}</p>
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={load}
                disabled={loading}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : loading ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            Loading exchange rates…
          </p>
        ) : rates && rates.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            No exchange rates are saved for this legal entity.
          </p>
        ) : rates ? (
          <div className="surface overflow-hidden rounded-2xl">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pair</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>As of</TableHead>
                    <TableHead>Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rates.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {row.base_currency} to {row.target_currency}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {formatRate(row.rate_parts_per_million)}
                      </TableCell>
                      <TableCell>{row.as_of}</TableCell>
                      <TableCell>{row.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
        <form onSubmit={(event) => void resolve(event)} className="grid gap-3">
          <h3 className="text-sm font-semibold">Look up a rate</h3>
          <div className="flex flex-wrap items-end gap-3">
            <label
              className="grid gap-1 text-xs font-medium"
              htmlFor="fx-lookup-base"
            >
              From
              <Input
                id="fx-lookup-base"
                value={lookupBase}
                maxLength={3}
                onChange={(event) => setLookupBase(event.target.value)}
              />
            </label>
            <label
              className="grid gap-1 text-xs font-medium"
              htmlFor="fx-lookup-target"
            >
              To
              <Input
                id="fx-lookup-target"
                value={lookupTarget}
                maxLength={3}
                onChange={(event) => setLookupTarget(event.target.value)}
              />
            </label>
            <label
              className="grid gap-1 text-xs font-medium"
              htmlFor="fx-lookup-date"
            >
              Used on
              <Input
                id="fx-lookup-date"
                type="date"
                value={lookupDate}
                onChange={(event) => setLookupDate(event.target.value)}
              />
            </label>
            <Button type="submit" variant="secondary">
              Look up
            </Button>
          </div>
          {lookup ? (
            <p className="text-sm text-[var(--muted-foreground)]">{lookup}</p>
          ) : null}
        </form>
      </section>
    </div>
  );
}
